/** @jsxImportSource preact */
/**
 * One cell of the harm severity scale, opened: what the rating means, and
 * side by side how the incidents rated exactly at that level and the
 * incidents rated at that level or worse are made up (donuts by any
 * stack-by dimension, or columns by year), one legend under both, then the
 * incidents themselves in the same sortable table the chart click-throughs
 * use, its severity column showing this category's direct rating rather
 * than the highest across categories. Counts are worded as incidents
 * ASSIGNED a rating: a Negligible rating is mostly "nothing reported", not
 * an established case. The donuts follow the incident overview page's pie: percentage
 * labels on leader lines, count and proportion on hover, legend below.
 * Direct-harm ratings only; the indirect ratings stay in the detail modal.
 */
import { h } from 'preact';
import { useEffect, useMemo, useRef, useState, useCallback } from 'preact/hooks';
import * as echarts from 'echarts';
import { Modal } from './Modal';
import { IncidentListTable, IncidentListItem } from './IncidentListModal';
import { IncidentDetailModal, IncidentDetail } from './IncidentDetailModal';
import { IMPACT_LABELS, IMPACT_PALETTE } from '@/data/impactScale';
import { HARM_SEVERITY_DEFINITIONS } from '@/data/harmSeverityScale';
import { getDomainColor, getSubdomainPaletteColor } from '@/data/incidentDomainColors';
import { getCategoryColor, getChartTheme, loadTheme, isMobileWidth } from '@/lib/theme';
import { formatTaxonomyLabel } from '../lib/taxonomyFormat';
import { isAiIdentityStateLabel, AI_IDENTITY_UNIDENTIFIED, AI_IDENTITY_NONE_NAMED } from '@/lib/aiIdentity';
import { lastUpdatedText } from '@/lib/lastUpdated';
import { SubdomainLegend } from './IncidentChart';

export interface SeverityCellRef {
  /** Category prefix, e.g. `physical`. */
  key: string;
  /** Category heading, e.g. "Physical Harm". */
  label: string;
  /** 1..5 */
  level: number;
}

interface SeverityCellModalProps {
  apiBase: string;
  dataset?: string;
  cell: SeverityCellRef;
  /** Incidents in the dataset, the denominator of the count lines. */
  totalIncidents: number | null;
  onClose: () => void;
}

type CompositionDim = 'domain' | 'subdomain' | 'allSubdomains' | 'entity' | 'intent' | 'timing' | 'aiGoal' | 'aiModel' | 'aiDeveloper' | 'euRisk' | 'harmCategory';
type Dim = CompositionDim | 'year';
/** `exact`: rated at the cell's level; `atLeast`: at that level or worse. */
type Scope = 'exact' | 'atLeast';

interface CellResponse {
  items: IncidentListItem[];
  composition: Record<CompositionDim, { data: Array<{ name: string; value: number }>; seriesMembers?: Record<string, string[]> }>;
  years: Record<string, number>;
  hasAllSubdomains?: boolean;
  totalIncidents?: number;
  lastUpdated?: string;
}

/** Dimension menu, in the order the other views offer them. */
const DIMS: Array<{ key: Dim; label: string; arcolaOnly?: boolean; multi?: boolean; note?: string }> = [
  { key: 'domain', label: 'Risk Domain' },
  { key: 'subdomain', label: 'Primary Subdomain' },
  { key: 'allSubdomains', label: 'All Subdomains', arcolaOnly: true, multi: true },
  { key: 'entity', label: 'Entity' },
  { key: 'intent', label: 'Intent' },
  { key: 'timing', label: 'Timing' },
  { key: 'aiGoal', label: 'AI Purpose' },
  { key: 'aiModel', label: 'AI Model Family', multi: true },
  { key: 'aiDeveloper', label: 'AI Developer', multi: true },
  { key: 'euRisk', label: 'EU AI Act Risk Tier' },
  { key: 'harmCategory', label: 'Other Harm Categories', multi: true, note: 'The other harm categories these incidents harmed at Minor severity or above, directly or indirectly.' },
  { key: 'year', label: 'Year' },
];

const YEAR_FLOOR = 2016;
/** Named slices before the rest is bundled into Other, per dimension: the
 * same limits the other views apply (subdomains are never collapsed). */
const TOP_N: Partial<Record<Dim, number>> = { aiGoal: 15, aiModel: 10, aiDeveloper: 10 };
const LEVEL_NAMES = IMPACT_LABELS.standard;

const STYLE_ID = 'gc-hs-cell-styles';
/** The exact / or-worse pair: count lines in one row, charts in the next,
 * so the two donuts sit level whatever the sentences wrap to; one column,
 * exact above or-worse, on a narrow screen. */
function injectCellStyles() {
  if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    .gc-hs-pair { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); grid-template-areas: "cl cr" "pl pr" "nl nr";
      column-gap: 16px; row-gap: 4px; align-items: start; }
    .gc-hs-pair.gc-hs-single { grid-template-columns: minmax(0, 1fr); grid-template-areas: "cl" "pl" "nl"; }
    .gc-hs-cl { grid-area: cl; } .gc-hs-cr { grid-area: cr; } .gc-hs-pl { grid-area: pl; }
    .gc-hs-pr { grid-area: pr; } .gc-hs-nl { grid-area: nl; } .gc-hs-nr { grid-area: nr; }
    @media (max-width: 719px) {
      .gc-hs-pair { grid-template-columns: minmax(0, 1fr); grid-template-areas: "cl" "pl" "nl" "cr" "pr" "nr"; }
      .gc-hs-pair .gc-hs-cr { margin-top: 14px; }
    }
  `;
  document.head.appendChild(style);
}
const levelColor = (level: number) => IMPACT_PALETTE[level - 1].bg;
const fmt = (n: number) => n.toLocaleString();
const pct = (n: number, of: number) => (of > 0 ? `${Math.round((100 * n) / of)}%` : '');
const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));

/** Tooltip placement for charts inside the modal: ECharts draws its tooltip
 * inside the chart element, where the modal box's overflow clipped it near
 * the edges, so it goes on the body instead, stacked above the modal. */
const MODAL_TOOLTIP = { appendTo: 'body', extraCssText: 'z-index: 2147483647;' } as const;
/** The modal's surface is light whatever the visitor's OS prefers (its
 * --gc-surface fallback is light and the site sets no dark theme), so its
 * charts take the light theme too; following the OS put light-grey axis
 * labels on a white panel. The legend is set via the same flag. */
const MODAL_IS_DARK = false;

/** Slice colour: domains and subdomains keep the site's domain palette; the
 * synthetic buckets stay grey; everything else takes the category palette. */
function sliceColor(name: string, dim: Dim, index: number, subdomainIndex: Map<string, number>): string {
  if (name === 'Other') return '#9ca3af';
  if ((dim === 'aiModel' || dim === 'aiDeveloper') && isAiIdentityStateLabel(name)) {
    if (name === AI_IDENTITY_UNIDENTIFIED) return '#6b7280';
    if (name === AI_IDENTITY_NONE_NAMED) return '#cbd5e1';
    return '#e5e7eb';
  }
  if (dim === 'domain') return getDomainColor(name);
  if (dim === 'subdomain' || dim === 'allSubdomains') return getSubdomainPaletteColor(name, subdomainIndex.get(name) ?? 0);
  return getCategoryColor(index);
}

interface Slice { name: string; label: string; value: number; color: string; members?: string[] }

/** A donut in the overview page's style: percentage labels on leader lines
 * (none under 1%, none at all on a phone once there are many slices), count
 * and proportion on hover, no built-in legend. */
function Donut({ slices, height = 250 }: { slices: Slice[]; height?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const chart = useRef<echarts.ECharts | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const c = chart.current ?? echarts.init(el);
    chart.current = c;
    const theme = getChartTheme(MODAL_IS_DARK);
    const mobile = isMobileWidth(window.innerWidth);
    const total = slices.reduce((s, x) => s + x.value, 0);
    const hideAllLabels = mobile && slices.length > 8;
    c.setOption({
      backgroundColor: 'transparent',
      useCoarsePointer: true,
      tooltip: {
        ...MODAL_TOOLTIP,
        show: !mobile,
        trigger: 'item',
        backgroundColor: theme.tooltipBg,
        borderColor: theme.tooltipBorder,
        textStyle: { color: theme.tooltipText, fontSize: 12 },
        formatter: (p: unknown) => {
          const q = p as { name: string; value: number; percent: number };
          return `${esc(q.name)}<br/>Count: ${fmt(q.value)}<br/>Proportion: ${q.percent.toFixed(1)}%`;
        },
      },
      legend: { show: false },
      series: [{
        type: 'pie',
        radius: mobile ? ['30%', '58%'] : ['34%', '64%'],
        center: ['50%', '50%'],
        data: slices.map((s) => {
          const d = { name: s.label, value: s.value, itemStyle: { color: s.color } };
          return total > 0 && (100 * s.value) / total >= 1 ? d : { ...d, label: { show: false }, labelLine: { show: false } };
        }),
        label: {
          show: !hideAllLabels,
          formatter: (p: unknown) => `${(((p as { value: number }).value / total) * 100).toFixed(1)}%`,
          fontSize: 11,
          color: 'inherit',
        },
        labelLine: { show: !hideAllLabels },
        emphasis: mobile ? { disabled: true } : { itemStyle: { shadowBlur: 10, shadowOffsetX: 0, shadowColor: 'rgba(0, 0, 0, 0.3)' } },
      }],
      animationDuration: 400,
    }, true);
    c.resize();
    const onResize = () => c.resize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [slices, height]);
  useEffect(() => () => { chart.current?.dispose(); chart.current = null; }, []);
  return <div ref={ref} style={{ width: '100%', height }} />;
}

/** Incidents per year from the floor, as columns in the level's colour.
 * `labelTip` puts a hover tooltip on one axis label (the "(to date)" year). */
function YearColumns({ labels, counts, color, max, labelTip, height = 250 }: { labels: string[]; counts: number[]; color: string; max?: number; labelTip?: { label: string; text: string } | null; height?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const chart = useRef<echarts.ECharts | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const c = chart.current ?? echarts.init(el);
    chart.current = c;
    const theme = getChartTheme(MODAL_IS_DARK);
    const rotate = labels.length > 8 ? 35 : 0;
    c.setOption({
      backgroundColor: 'transparent',
      tooltip: {
        ...MODAL_TOOLTIP,
        trigger: 'axis',
        backgroundColor: theme.tooltipBg,
        borderColor: theme.tooltipBorder,
        textStyle: { color: theme.tooltipText, fontSize: 12 },
        formatter: (p: unknown) => { const q = (p as Array<{ name: string; value: number }>)[0]; return `${esc(q.name)}<br/>Count: ${fmt(q.value)}`; },
      },
      grid: { left: 40, right: 12, top: 12, bottom: rotate ? 58 : 30 },
      xAxis: {
        type: 'category', data: labels, axisLabel: { fontSize: 11, color: theme.muted, interval: 0, rotate }, axisLine: { lineStyle: { color: theme.grid } }, axisTick: { show: false },
        // Every label turns hoverable once this is on; '' shows no box.
        tooltip: { show: Boolean(labelTip), formatter: (p: { value?: string }) => (labelTip && p.value === labelTip.label ? esc(labelTip.text) : '') },
      },
      // One y-axis for both scopes, so the columns compare across the pair.
      yAxis: { type: 'value', minInterval: 1, max, axisLabel: { fontSize: 11, color: theme.muted }, splitLine: { lineStyle: { color: theme.grid } } },
      series: [{ type: 'bar', data: counts, itemStyle: { color, borderRadius: [3, 3, 0, 0] }, barMaxWidth: 36 }],
      animationDuration: 400,
    }, true);
    c.resize();
    const onResize = () => c.resize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [labels, counts, color, max, labelTip?.label, labelTip?.text, height]);
  useEffect(() => () => { chart.current?.dispose(); chart.current = null; }, []);
  return <div ref={ref} style={{ width: '100%', height }} />;
}

export function SeverityCellModal({ apiBase, dataset, cell, totalIncidents, onClose }: SeverityCellModalProps) {
  const ui = loadTheme().colors.ui;
  const levelName = LEVEL_NAMES[cell.level];
  const hasWider = cell.level < 5;
  const scopes: Scope[] = hasWider ? ['exact', 'atLeast'] : ['exact'];
  const cap = cell.key.replace(/^./, (c) => c.toUpperCase());

  const [dim, setDim] = useState<Dim>('domain');
  const [responses, setResponses] = useState<Partial<Record<Scope, CellResponse>>>({});
  const [errors, setErrors] = useState<Partial<Record<Scope, string>>>({});
  const [fetchAttempt, setFetchAttempt] = useState(0);
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [listScope, setListScope] = useState<Scope>('exact');

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailId, setDetailId] = useState('');
  const [detailIncident, setDetailIncident] = useState<IncidentDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => { injectCellStyles(); }, []);

  // Both scopes are fetched together when the modal opens.
  useEffect(() => {
    let cancelled = false;
    setErrors({});
    for (const scope of scopes) {
      const qs = new URLSearchParams();
      if (dataset) qs.set('src', dataset);
      qs.set('chartType', 'severity-cell');
      qs.set(scope === 'atLeast' ? `minSeverity${cap}` : `severity${cap}`, String(cell.level));
      fetch(`${apiBase}/data/incidents?${qs}`)
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`API error: ${r.status}`))))
        .then((json: CellResponse) => { if (!cancelled) setResponses((m) => ({ ...m, [scope]: json })); })
        .catch((err: Error) => { if (!cancelled) setErrors((m) => ({ ...m, [scope]: err.message || 'Could not load these incidents' })); });
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase, dataset, cell.key, cell.level, fetchAttempt]);

  const exact = responses.exact;
  const total = totalIncidents ?? exact?.totalIncidents ?? responses.atLeast?.totalIncidents ?? null;
  const dims = useMemo(() => DIMS.filter((d) => !d.arcolaOnly || exact?.hasAllSubdomains), [exact?.hasAllSubdomains]);
  const dimMeta = DIMS.find((d) => d.key === dim) ?? DIMS[0];

  // Both donuts show the same categories: the long tail is chosen once,
  // ranked on the wider scope (a superset of the exact one), keeping the
  // ten largest values plus the AI-identity state labels, as the other
  // views do; everything else is bundled into Other in both. Names keep
  // the API's order (canonical for domains and subdomains) and get one
  // colour and one legend entry each.
  const collapsed = useMemo(() => {
    if (dim === 'year') return { kept: [] as string[], others: [] as string[] };
    const pinned = (n: string) => (dim === 'aiModel' || dim === 'aiDeveloper') && isAiIdentityStateLabel(n);
    const order: string[] = [];
    for (const scope of ['atLeast', 'exact'] as Scope[]) {
      for (const r of responses[scope]?.composition[dim]?.data ?? []) if (!order.includes(r.name)) order.push(r.name);
    }
    const ranking = responses.atLeast?.composition[dim]?.data ?? responses.exact?.composition[dim]?.data ?? [];
    const candidates = ranking.filter((r) => !pinned(r.name));
    const limit = TOP_N[dim];
    const keep = new Set(!limit || candidates.length <= limit ? candidates.map((r) => r.name)
      : [...candidates].sort((a, b) => b.value - a.value).slice(0, limit).map((r) => r.name));
    return { kept: order.filter((n) => pinned(n) || keep.has(n)), others: order.filter((n) => !pinned(n) && !keep.has(n)) };
  }, [responses, dim]);
  const names = useMemo(() => (collapsed.others.length ? [...collapsed.kept, 'Other'] : collapsed.kept), [collapsed]);
  const colorByName = useMemo(() => {
    const subdomainIndex = new Map<string, number>();
    const perDomain = new Map<string, number>();
    for (const n of names) {
      const domainNum = n.match(/^(\d+)\./)?.[1];
      if (domainNum) { subdomainIndex.set(n, perDomain.get(domainNum) ?? 0); perDomain.set(domainNum, (perDomain.get(domainNum) ?? 0) + 1); }
    }
    return new Map(names.map((n, i) => [n, sliceColor(n, dim, i, subdomainIndex)]));
  }, [names, dim]);
  const membersOf = (name: string): string[] | undefined => (name === 'Other' && collapsed.others.length ? collapsed.others : undefined);
  const labelOf = (name: string) => (name === 'Other' ? `Other (${collapsed.others.length})` : formatTaxonomyLabel(name));

  const slicesFor = (scope: Scope): Slice[] => {
    const rows = responses[scope]?.composition[dim as CompositionDim]?.data ?? [];
    const byName = new Map(rows.map((r) => [r.name, r.value]));
    const out: Slice[] = collapsed.kept
      .filter((n) => (byName.get(n) ?? 0) > 0 && !hidden.has(n))
      .map((n) => ({ name: n, label: labelOf(n), value: byName.get(n) ?? 0, color: colorByName.get(n) ?? '#9ca3af' }));
    const other = collapsed.others.reduce((s, n) => s + (byName.get(n) ?? 0), 0);
    if (other > 0 && !hidden.has('Other')) out.push({ name: 'Other', label: labelOf('Other'), value: other, color: '#9ca3af', members: collapsed.others });
    return out;
  };

  // Years from the floor to the latest seen in either scope, zero-filled.
  const yearAxis = useMemo(() => {
    const thisYear = new Date().getUTCFullYear();
    let last = YEAR_FLOOR;
    for (const scope of scopes) for (const y of Object.keys(responses[scope]?.years ?? {})) { const n = parseInt(y, 10); if (Number.isFinite(n)) last = Math.max(last, n); }
    const years: number[] = [];
    for (let y = YEAR_FLOOR; y <= last; y++) years.push(y);
    const toDate = `${thisYear} (to date)`;
    const stamp = scopes.map((s) => responses[s]?.lastUpdated).find(Boolean);
    const tipText = lastUpdatedText(stamp);
    return {
      years,
      labels: years.map((y) => (y === thisYear ? toDate : String(y))),
      toDateTip: tipText ? { label: toDate, text: tipText } : null,
    };
  }, [responses, scopes]);
  const yearCounts = (scope: Scope) => {
    const ys = responses[scope]?.years ?? {};
    const counts = yearAxis.years.map((y) => ys[String(y)] ?? 0);
    const before = Object.entries(ys).reduce((s, [y, n]) => (parseInt(y, 10) < YEAR_FLOOR ? s + n : s), 0);
    return { counts, before };
  };
  // The pair share a y-axis, rounded up to a tidy step above the taller.
  const yearMax = useMemo(() => {
    const top = Math.max(0, ...scopes.map((s) => Math.max(0, ...yearCounts(s).counts)));
    if (top === 0) return undefined;
    const p = Math.pow(10, Math.floor(Math.log10(top)));
    const step = p >= 10 ? p / 2 : p;
    return Math.ceil(top / step) * step;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responses, yearAxis, scopes]);

  const handleIncidentClick = useCallback((incidentId: string) => {
    setDetailId(incidentId);
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailIncident(null);
    const qs = new URLSearchParams();
    if (dataset) qs.set('src', dataset);
    qs.set('chartType', 'incident-detail');
    qs.set('incidentId', incidentId);
    fetch(`${apiBase}/data/incidents?${qs}`)
      .then((r) => r.json())
      .then((json: { incident?: IncidentDetail }) => setDetailIncident(json?.incident ?? null))
      .catch((err) => console.error('Failed to fetch incident detail:', err))
      .finally(() => setDetailLoading(false));
  }, [apiBase, dataset]);

  const scopeWord = (scope: Scope) => (scope === 'atLeast' ? `${levelName} or worse` : levelName);
  const countLine = (scope: Scope) => {
    const data = responses[scope];
    if (errors[scope]) {
      return (
        <span style={{ color: ui.error }}>
          {errors[scope]}{' '}
          <button type="button" style={quietBtn} onClick={() => setFetchAttempt((n) => n + 1)}>Retry</button>
        </span>
      );
    }
    if (!data) return <span style={{ color: ui.textMuted }}>Loading incidents{'…'}</span>;
    const n = data.items.length;
    return (
      <span>
        <strong>{fmt(n)}</strong>{total != null ? ` of ${fmt(total)} (${pct(n, total)})` : ''} incident{n === 1 ? ' is' : 's are'} assigned {scopeWord(scope)} for direct {cell.label.toLowerCase()}.
      </span>
    );
  };

  const quietBtn = { font: 'inherit', fontSize: 12, border: `1px solid ${ui.border}`, borderRadius: 6, padding: '2px 8px', background: ui.background, color: ui.text, cursor: 'pointer' };
  const seg = { display: 'inline-flex', border: `1px solid ${ui.border}`, borderRadius: 6, overflow: 'hidden' };
  const segBtn = (on: boolean) => ({
    font: 'inherit', fontSize: 13, border: 0, padding: '5px 12px', cursor: 'pointer',
    background: on ? ui.text : ui.background, color: on ? ui.background : ui.text,
  });
  const listData = responses[listScope];
  const anyData = scopes.some((s) => responses[s]);

  return (
    <Modal isOpen onClose={onClose} title={`${cell.label} — ${levelName}`} width="min(1100px, 94vw)">
      {/* A column that can shrink below its content: the incident table has
          fixed column widths, and an implicit auto column would size itself
          to the table and lay the sections above it out at that width too,
          which on a phone drew the donuts off the right edge. With minmax(0,
          1fr) the table scrolls inside its own box instead. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 14, minWidth: 0, maxWidth: '100%' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          <span style={{ background: levelColor(cell.level), color: cell.level >= 4 ? '#fff' : '#111827', borderRadius: 4, padding: '2px 9px', fontSize: 13, fontWeight: 700 }}>
            {levelName}
          </span>
          <span style={{ fontSize: 13.5, maxWidth: '80ch' }}>{HARM_SEVERITY_DEFINITIONS[cell.key]?.[cell.level - 1]}</span>
        </div>

        <section style={{ border: `1px solid ${ui.border}`, borderRadius: 8, padding: '12px 14px', background: ui.background, display: 'grid', gap: 12 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 12px', alignItems: 'center', justifyContent: 'space-between' }}>
            <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>How the incidents break down</h3>
            <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: 13, color: ui.textMuted }}>
              By
              <select
                id={`gc-hs-dim-${cell.key}-${cell.level}`}
                value={dim}
                onChange={(e) => { setDim((e.currentTarget as HTMLSelectElement).value as Dim); setHidden(new Set()); }}
                style={{ font: 'inherit', fontSize: 13, color: ui.text, background: ui.background, border: `1px solid ${ui.border}`, borderRadius: 6, padding: '4px 8px' }}
              >
                {dims.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
              </select>
            </label>
          </div>

          <div class={`gc-hs-pair${scopes.length === 1 ? ' gc-hs-single' : ''}`}>
            {scopes.map((scope) => {
              const data = responses[scope];
              const n = data?.items.length ?? 0;
              const side = scope === 'exact' ? 'l' : 'r';
              const yc = data && dim === 'year' ? yearCounts(scope) : null;
              return [
                <div key={`c${side}`} class={`gc-hs-c${side}`} style={{ fontSize: 13.5, lineHeight: 1.4 }} aria-live="polite">{countLine(scope)}</div>,
                <div key={`p${side}`} class={`gc-hs-p${side}`}>
                  {data && n > 0 && dim !== 'year' && <Donut slices={slicesFor(scope)} />}
                  {data && n > 0 && yc && <YearColumns labels={yearAxis.labels} counts={yc.counts} color={levelColor(cell.level)} max={yearMax} labelTip={yearAxis.toDateTip} />}
                  {data && n === 0 && <div style={{ height: 120, display: 'grid', placeItems: 'center', fontSize: 13, color: ui.textMuted }}>No incidents at this level.</div>}
                </div>,
                <div key={`n${side}`} class={`gc-hs-n${side}`} style={{ fontSize: 12, color: ui.textMuted }}>
                  {yc ? `Showing incidents since ${YEAR_FLOOR}${yc.before > 0 ? `; ${fmt(yc.before)} earlier incident${yc.before === 1 ? '' : 's'} not shown` : ''}.` : null}
                </div>,
              ];
            })}
          </div>

          {dim !== 'year' && names.length > 0 && (
            <div>
              <div style={{ marginTop: 2, marginBottom: 6 }}>
                <div style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.3 }}>{dimMeta.label}</div>
                {(dimMeta.note || dimMeta.multi) && (
                  <div style={{ fontSize: 11, fontStyle: 'italic', opacity: 0.8, marginTop: 3, maxWidth: '85ch', lineHeight: 1.4 }}>
                    {dimMeta.note ? `${dimMeta.note} ` : ''}{dimMeta.multi ? 'An incident appears under each value it carries, so the slices add up to more than the incident count.' : ''}
                  </div>
                )}
                <div style={{ fontSize: 10, fontStyle: 'italic', opacity: 0.75, marginTop: 3 }}>Click categories to toggle visibility</div>
              </div>
              {dim === 'subdomain' || dim === 'allSubdomains' ? (
                // The overview page's three-column legend, grouped by domain,
                // with the group heading toggling the whole domain.
                <SubdomainLegend
                  seriesNames={names}
                  hiddenSeries={hidden}
                  onToggle={(toggled, currentlyHidden) => setHidden((h) => {
                    const next = new Set(h);
                    for (const n of toggled) { if (currentlyHidden) next.delete(n); else next.add(n); }
                    return next;
                  })}
                  chartTheme={getChartTheme(MODAL_IS_DARK)}
                  isMobile={isMobileWidth(window.innerWidth)}
                />
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                  {names.map((name) => {
                    const isHidden = hidden.has(name);
                    const members = membersOf(name);
                    return (
                      <div
                        key={name}
                        onClick={() => setHidden((h) => { const next = new Set(h); if (next.has(name)) next.delete(name); else next.add(name); return next; })}
                        title={members?.length ? `Other: ${members.map(formatTaxonomyLabel).join(', ')}` : undefined}
                        style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', opacity: isHidden ? 0.35 : 1, fontSize: 12, userSelect: 'none' }}
                      >
                        <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: 2, background: colorByName.get(name), flexShrink: 0 }} />
                        {labelOf(name)}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </section>

        {anyData && (
          <section>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 12px', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>The incidents</h3>
              {hasWider && (
                <div style={seg} role="group" aria-label="Which incidents to list">
                  {scopes.map((scope) => (
                    <button key={scope} type="button" style={segBtn(listScope === scope)} aria-pressed={listScope === scope} onClick={() => setListScope(scope)}>
                      Assigned {scopeWord(scope)}{responses[scope] ? ` (${fmt(responses[scope]!.items.length)})` : ''}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div style={{ overflowX: 'auto', minWidth: 0 }}>
              <IncidentListTable
                items={listData?.items ?? []}
                loading={!listData}
                onIncidentClick={handleIncidentClick}
                severityColumn={{ label: /\(CSEA\)$/.test(cell.label) ? 'CSEA' : cell.label }}
                activeFilters={[
                  { label: 'Harm category', values: [cell.label] },
                  { label: 'Direct severity', values: [scopeWord(listScope)] },
                ]}
              />
            </div>
          </section>
        )}
      </div>

      <IncidentDetailModal
        isOpen={detailOpen}
        onClose={() => setDetailOpen(false)}
        incidentId={detailId}
        incident={detailIncident}
        loading={detailLoading}
      />
    </Modal>
  );
}
